// Defaults & Workout rebuild — state-machine transition control. Replaces
// the raw <details> picker (which had no outside-click dismiss) with a
// kit-pattern dropdown; each selection opens a confirmation modal whose
// body explains the downstream consequence. The catastrophic transitions
// (acceleration / charge-off / final closure) are now ALSO enforced
// server-side via Default.WriteOff — the FE gate mirrors that permission.

import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import { Button } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { useAuth } from "@/hooks/useAuth";
import {
  useDefaultCaseStateMachine,
  useTransitionDefaultCase,
} from "@/hooks/portfolioAdmin/usePr4b";
import { formatCaseStatus } from "./caseStages";
import { usePendingEscapeHatch } from "./usePendingEscapeHatch";

interface TransitionStateControlProps {
  caseId: string;
  loanId: string;
  currentState: string;
}

/** Per-transition user-facing consequence text. Keep terse but specific. */
const TRANSITION_CONSEQUENCE: Record<string, string> = {
  "notice-of-default-sent":
    "Generates the Notice-of-Default document and starts the cure period.",
  "cure-period-active":
    "Activates the cure-period countdown. Workers will auto-expire if not cured.",
  "cured-restored":
    "Marks the borrower as cured. The loan returns to performing status.",
  "cure-period-expired":
    "Marks the cure period as expired. Forbearance or acceleration is the next step.",
  "forbearance-executed":
    "Records that a Forbearance Agreement has been signed and is in effect.",
  "workout-in-progress":
    "Marks the case as in active workout. Costs and recoveries accumulate here.",
  "acceleration-declared":
    "Declares the full loan balance immediately due and payable. E1 approval required.",
  "demand-letter-sent":
    "Records that the formal demand letter has been delivered to the borrower.",
  "replevin-initiated":
    "Records that a replevin (collateral seizure) action has been filed.",
  "judicial-foreclosure-filed":
    "Records that judicial foreclosure has been filed in court.",
  "receiver-appointed":
    "Records that a receiver has been appointed over the borrower's assets.",
  "article-9-disposition-initiated":
    "Begins the §9-610 / §9-621 disposition process. Notice periods start.",
  "deed-in-lieu-accepted":
    "Records that the borrower has tendered a deed in lieu of foreclosure.",
  "bill-of-sale-in-lieu-accepted":
    "Records that the borrower has tendered a bill of sale in lieu of foreclosure.",
  "guaranty-enforcement-initiated":
    "Begins enforcement against the guarantor(s) named on the loan.",
  "recovery-applied":
    "Records that recovery proceeds have been applied to the loan balance.",
  "charged-off":
    "Charges off the remaining balance as a loss. E1 approval required.",
  "deficiency-claim-pursued":
    "Pursues a deficiency claim against the borrower for unrecovered balance.",
  "resolved-recovered":
    "Marks the case as resolved with substantial recovery.",
  "closed-final":
    "Closes the case permanently. No further events may be added.",
};

/** Spec §14 — E1-only transitions, enforced server-side via Default.WriteOff. */
export const E1_ONLY_TRANSITIONS = new Set([
  "acceleration-declared",
  "charged-off",
  "closed-final",
]);

export function TransitionStateControl({
  caseId,
  loanId,
  currentState,
}: TransitionStateControlProps) {
  const auth = useAuth();
  const stateMachineQuery = useDefaultCaseStateMachine(caseId);
  const transitionMutation = useTransitionDefaultCase(caseId, loanId);

  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  const { canDismiss, pendingNotice } = usePendingEscapeHatch(
    transitionMutation.isPending,
    error !== null,
  );

  // Outside-click dismiss (the old <details> picker stayed open forever).
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const allowed = stateMachineQuery.data?.allowedTransitions ?? [];

  const canManage =
    auth.hasPermission("Defaults.Manage") || auth.hasRoleCode("ADMIN");
  const canWriteOff =
    auth.hasPermission("Default.WriteOff") ||
    auth.hasRoleCode("E1") ||
    auth.hasRoleCode("ADMIN");

  const visibleTransitions = allowed.filter((t) =>
    E1_ONLY_TRANSITIONS.has(t) ? canWriteOff : canManage,
  );

  const closeConfirm = useCallback(() => {
    if (!canDismiss) return;
    setPending(null);
    setReason("");
    setError(null);
  }, [canDismiss]);

  const handleConfirm = async () => {
    if (!pending) return;
    setError(null);
    try {
      await transitionMutation.mutateAsync({
        toState: pending,
        reason: reason.trim() || `Manual transition to ${pending}`,
      });
      setPending(null);
      setReason("");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Transition failed");
    }
  };

  if (!canManage || visibleTransitions.length === 0) return null;

  return (
    <div
      ref={rootRef}
      className="relative"
      data-testid="state-machine-picker"
      onKeyDown={(e) => {
        // Escape closes from anywhere inside the control (trigger OR a
        // focused menu item), not just the trigger.
        if (e.key === "Escape") setOpen(false);
      }}
    >
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="cursor-pointer inline-flex items-center gap-space-1 px-space-3 h-8 bg-onyx text-white-pure rounded-sm text-label font-display font-bold uppercase tracking-button"
        data-testid="state-machine-picker-trigger"
      >
        Transition state <ChevronDown size={14} aria-hidden />
      </button>
      {open && (
        <ul
          className="absolute right-0 mt-space-1 z-40 min-w-[18rem] bg-white-pure border border-border-light rounded-md shadow-lg p-space-2"
          role="menu"
        >
          <li className="px-space-2 py-space-1 text-caption text-gray-mid">
            From <strong>{formatCaseStatus(currentState)}</strong> →
          </li>
          {visibleTransitions.map((t) => (
            <li key={t}>
              <button
                type="button"
                role="menuitem"
                className="block w-full text-left px-space-2 py-space-2 hover:bg-white-warm rounded text-small"
                onClick={() => {
                  setPending(t);
                  setOpen(false);
                }}
                data-testid={`transition-option-${t}`}
              >
                {formatCaseStatus(t)}
                {E1_ONLY_TRANSITIONS.has(t) && (
                  <span className="ml-space-1 text-caption text-status-error">
                    (E1)
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}

      <Modal
        open={!!pending}
        onClose={closeConfirm}
        dismissOnBackdropClick={false}
        title={`Transition to ${pending ? formatCaseStatus(pending) : ""}`}
        description={pending ? TRANSITION_CONSEQUENCE[pending] : undefined}
        footer={
          <>
            <Button
              variant="tertiary"
              size="sm"
              onClick={closeConfirm}
              disabled={!canDismiss}
            >
              Cancel
            </Button>
            <Button
              variant={
                pending && E1_ONLY_TRANSITIONS.has(pending)
                  ? "destructive"
                  : "primary"
              }
              size="sm"
              onClick={handleConfirm}
              disabled={transitionMutation.isPending || reason.trim().length < 5}
              data-testid="state-machine-confirm"
            >
              {transitionMutation.isPending ? "Transitioning…" : "Confirm"}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-space-2">
          <label htmlFor="transition-reason" className="text-small font-bold">
            Reason (required, ≥ 5 characters)
          </label>
          <textarea
            id="transition-reason"
            rows={3}
            className="w-full border border-border-light rounded-md p-space-2 text-small"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why is this transition being made?"
          />
          {error && (
            <p className="text-small text-status-error m-0" role="alert">
              {error}
            </p>
          )}
          {pendingNotice && (
            <p className="text-caption text-gray-mid m-0" role="status">
              {pendingNotice}
            </p>
          )}
        </div>
      </Modal>
    </div>
  );
}
